import React, { useState, useEffect, useRef } from 'react';
import { Building, Save, Loader2, PlayCircle, StopCircle, Upload, Image } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useOrganization, Organization } from '../../contexts/OrganizationContext';
import { put, putFormData } from '../../api/apiClient';
import AudioRecordButton from '../common/AudioRecordButton';
import { aiApi } from '../../api/ai';

const OrganizationSettings = () => {
  const { t } = useLanguage();
  const { organization: contextOrg, loading: orgLoading, refreshOrganization } = useOrganization();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Form state
  const [name, setName] = useState('');
  const [currentLandscape, setCurrentLandscape] = useState('');
  
  // Logo state
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [logoSuccess, setLogoSuccess] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  // Audio recording state
  const [isTranscribing, setIsTranscribing] = useState(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  useEffect(() => {
    if (contextOrg) {
      setOrganization(contextOrg);
      setName(contextOrg.name || '');
      setCurrentLandscape(contextOrg.current_landscape || '');
      setLoading(false);
    } else {
      setLoading(orgLoading);
    }
  }, [contextOrg, orgLoading]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setSaving(true);
      setError(null);
      setSuccess(false);

      const updatedOrg = await put<Organization>('organization/', {
        name,
        current_landscape: currentLandscape,
      });
      
      // Make sure to preserve the logo URL if it exists in the current organization state
      if (organization?.logo && !updatedOrg.logo) {
        updatedOrg.logo = organization.logo;
      }
      
      setOrganization(updatedOrg);
      
      // Don't need to refresh the global organization context here since we're only
      // updating name and landscape, not the logo that is shown in the header
      
      setSuccess(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Error updating organization:', err);
      setError(t('settings.organization.error.update'));
    } finally {
      setSaving(false);
    }
  };

  const handleRecordingComplete = async (audioBlob: Blob) => {
    // Create a URL for the audio blob
    const url = URL.createObjectURL(audioBlob);
    setAudioUrl(url);
    
    // Start transcription
    try {
      setIsTranscribing(true);
      const { text } = await aiApi.transcribeAudio(audioBlob);
      setCurrentLandscape(text);
    } catch (err) {
      console.error('Error transcribing audio:', err);
      setError(t('settings.organization.error.transcription'));
    } finally {
      setIsTranscribing(false);
    }
  };

  const toggleAudioPlayback = () => {
    if (!audioRef.current || !audioUrl) return;
    
    if (isPlaying) {
      audioRef.current.pause();
      setIsPlaying(false);
    } else {
      audioRef.current.play();
      setIsPlaying(true);
    }
  };
  
  const handleLogoClick = () => {
    fileInputRef.current?.click();
  };
  
  const handleLogoChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    // Validate file type
    const validTypes = ['image/jpeg', 'image/png', 'image/svg+xml'];
    if (!validTypes.includes(file.type)) {
      setLogoError(t('settings.organization.error.invalidFileType'));
      return;
    }
    
    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      setLogoError(t('settings.organization.error.fileTooBig'));
      return;
    }
    
    try {
      setUploadingLogo(true);
      setLogoError(null);
      setLogoSuccess(false);
      
      const formData = new FormData();
      formData.append('logo', file);
      
      const updatedOrg = await putFormData<Organization>('organization/update-logo', formData);
      
      // Update organization in local state
      setOrganization(updatedOrg);
      
      // Refresh organization in the context so all components get the updated logo
      await refreshOrganization();
      
      setLogoSuccess(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setLogoSuccess(false);
      }, 3000);
    } catch (err) {
      console.error('Error uploading logo:', err);
      setLogoError(t('settings.organization.logoError'));
    } finally {
      setUploadingLogo(false);
    }
  };

  // Initialize audio element when audioUrl changes
  useEffect(() => {
    if (audioUrl) {
      if (!audioRef.current) {
        audioRef.current = new Audio(audioUrl);
      } else {
        audioRef.current.src = audioUrl;
        audioRef.current.load();
      }
    }
  }, [audioUrl]);
  
  // Handle audio playback ended
  useEffect(() => {
    const handleEnded = () => {
      setIsPlaying(false);
    };
    
    if (audioRef.current) {
      // Make sure we remove any existing listener first to avoid duplicates
      audioRef.current.removeEventListener('ended', handleEnded);
      audioRef.current.addEventListener('ended', handleEnded);
    }
    
    return () => {
      if (audioRef.current) {
        audioRef.current.removeEventListener('ended', handleEnded);
      }
    };
  }, [audioUrl, audioRef.current]); // Re-attach listener when audioUrl or audio element changes

  if (loading) {
    return (
      <div className="bg-white rounded-lg border shadow-sm p-6 flex items-center justify-center">
        <Loader2 className="w-6 h-6 text-blue-600 animate-spin mr-2" />
        <span className="text-gray-600">{t('common.loading')}</span>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg border shadow-sm">
      <div className="px-6 py-4 border-b">
        <h2 className="text-lg font-semibold text-gray-800 flex items-center">
          <Building className="w-5 h-5 text-blue-600 mr-2" />
          {t('settings.organization.title')}
        </h2>
        <p className="text-sm text-gray-500">{t('settings.organization.subtitle')}</p>
      </div>

      <div className="p-6">
        <form onSubmit={handleSubmit} className="w-full">
          {error && (
            <div className="mb-6 bg-red-50 border border-red-200 text-red-600 p-3 rounded-lg text-sm">
              {error}
            </div>
          )}
          
          {success && (
            <div className="mb-6 bg-green-50 border border-green-200 text-green-600 p-3 rounded-lg text-sm">
              {t('settings.organization.success')}
            </div>
          )}

          <div className="space-y-6">
            <div className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {t('settings.organization.name')}
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-blue-50/30"
                  placeholder={t('settings.organization.namePlaceholder')}
                  required
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('settings.organization.companyLogo')}
                </label>
                
                {logoError && (
                  <div className="mb-3 bg-red-50 border border-red-200 text-red-600 p-3 rounded-lg text-sm">
                    {logoError}
                  </div>
                )}
                
                {logoSuccess && (
                  <div className="mb-3 bg-green-50 border border-green-200 text-green-600 p-3 rounded-lg text-sm">
                    {t('settings.organization.logoSuccess')}
                  </div>
                )}
                
                <div className="flex items-start space-x-5">
                  <div 
                    className="relative bg-gray-100 border border-gray-300 rounded-lg w-32 h-32 flex items-center justify-center overflow-hidden cursor-pointer hover:bg-gray-200 transition-colors"
                    onClick={handleLogoClick}
                  >
                    {uploadingLogo ? (
                      <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
                    ) : organization?.logo ? (
                      <img 
                        src={`${organization.logo}?t=${new Date().getTime()}`} 
                        alt={organization.name || 'Company logo'} 
                        className="w-full h-full object-contain"
                      />
                    ) : (
                      <Image className="w-12 h-12 text-gray-400" />
                    )}
                    
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png, image/jpeg, image/svg+xml"
                      className="hidden"
                      onChange={handleLogoChange}
                    />
                  </div>
                  
                  <div className="flex-1">
                    <button
                      type="button"
                      onClick={handleLogoClick}
                      disabled={uploadingLogo}
                      className="mb-2 inline-flex items-center px-4 py-2 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200 transition-colors"
                    >
                      {uploadingLogo ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          {t('settings.organization.uploading')}
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          {t('settings.organization.uploadLogo')}
                        </>
                      )}
                    </button>
                    <p className="text-sm text-gray-600">
                      {t('settings.organization.logoHint')}
                    </p>
                  </div>
                </div>
              </div>
              
              <div>
                <div className="flex items-center mb-2">
                  <label className="text-sm font-medium text-gray-700">
                    {t('settings.organization.currentLandscape')}
                  </label>
                </div>

                <div className="mb-3 inline-flex items-center bg-gradient-to-r from-blue-50 to-blue-50/30 px-4 py-2 rounded-lg border border-blue-100 shadow-sm">
                  <div className="flex items-center gap-3">
                    <AudioRecordButton 
                      onRecordingComplete={handleRecordingComplete}
                      isProcessing={isTranscribing}
                      className="flex-none"
                    />
                    
                    {audioUrl && (
                      <button
                        type="button"
                        onClick={toggleAudioPlayback}
                        className={`inline-flex items-center gap-1.5 text-sm px-3 py-1.5 rounded-full transition-colors ${
                          isPlaying 
                            ? 'bg-red-100 text-red-600 hover:bg-red-200' 
                            : 'bg-blue-100 text-blue-600 hover:bg-blue-200'
                        }`}
                      >
                        {isPlaying ? (
                          <>
                            <StopCircle className="w-4 h-4" />
                            {t('common.audioRecord.stopPlayback')}
                          </>
                        ) : (
                          <>
                            <PlayCircle className="w-4 h-4" />
                            {t('common.audioRecord.playRecording')}
                          </>
                        )}
                      </button>
                    )}
                    
                    <div className="relative">
                      <button 
                        type="button"
                        className="inline-flex items-center justify-center h-7 w-7 rounded-full bg-white/80 text-blue-600 hover:bg-white hover:text-blue-700 border border-blue-100 transition-colors"
                        aria-label={t('settings.organization.voiceInstructionsTitle')}
                        onMouseEnter={(e) => {
                          const tooltip = e.currentTarget.nextElementSibling as HTMLElement;
                          if (tooltip) {
                            tooltip.style.display = 'block';
                          }
                        }}
                        onMouseLeave={(e) => {
                          const tooltip = e.currentTarget.nextElementSibling as HTMLElement;
                          if (tooltip) {
                            tooltip.style.display = 'none';
                          }
                        }}
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-4 h-4">
                          <circle cx="12" cy="12" r="10"></circle>
                          <path d="M12 16v-4"></path>
                          <path d="M12 8h.01"></path>
                        </svg>
                      </button>
                      <div 
                        className="hidden absolute top-0 left-9 z-50 bg-white shadow-lg border border-gray-200 text-gray-700 text-xs rounded-md p-3 w-64"
                      >
                        <div className="font-semibold mb-1 text-blue-600">{t('settings.organization.voiceInstructionsTitle')}</div>
                        <p>{t('settings.organization.voiceInstructions')}</p>
                      </div>
                    </div>
                  </div>
                </div>
                <textarea
                  value={currentLandscape}
                  onChange={(e) => setCurrentLandscape(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all bg-blue-50/30 min-h-[180px] resize-y"
                  placeholder={t('settings.organization.landscapePlaceholder')}
                />
                <div className="mt-2 bg-blue-50/50 p-3 rounded-lg border border-blue-100">
                  <p className="text-sm text-gray-600">
                    {t('settings.organization.landscapeHint')}
                  </p>
                </div>
              </div>
              
              {/* Organization information section has been hidden */}
            </div>

            <div className="pt-6 mt-4">
              <div className="flex items-center border-t border-gray-100 pt-4">
                <div className="flex-grow"></div>
                <button
                  type="submit"
                  className="px-5 py-2.5 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm flex items-center"
                  disabled={saving}
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      {t('settings.organization.saving')}
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      {t('settings.organization.save')}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default OrganizationSettings;